import * as React from "react";
import { useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Search } from "lucide-react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Button } from "components/common/buttons/Button";
import { cn } from "utils/cn";

const inputVariants = cva(
  [
    "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground",
    "flex min-w-0 rounded-md bg-transparent text-base shadow-xs transition-[color,box-shadow] outline-none",
    "focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px]",
    "aria-invalid:ring-danger/20 aria-invalid:border-danger",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  ],
  {
    variants: {
      layout: {
        row: "flex-row items-center gap-4",
        column: "flex-col items-start gap-1",
      },
      error: {
        true: "border-danger focus-visible:border-danger",
        false: "",
      },
    },
    defaultVariants: {
      layout: "column",
      error: false,
    },
  }
);

export interface InputV3Props
  extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {
  label?: string;
  layout?: "row" | "column";
  error?: boolean;
  errorMessage?: string;
  className?: string;
  /** Extra classes for the inner input element (e.g. font-mono, truncate). */
  inputClassName?: string;
  /** Renders inside the field on the right (e.g. copy). Not used when type is password (eye toggle owns the slot). */
  endAdornment?: React.ReactNode;
}

export const InputV3 = React.forwardRef<HTMLInputElement, InputV3Props>(
  (
    {
      label,
      layout = "column",
      error,
      errorMessage,
      className,
      inputClassName,
      endAdornment,
      id,
      type,
      value,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || (label ? `input-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined);
    const isSearchType = type === "search";
    const isPasswordType = type === "password";
    const showEndAdornment = Boolean(endAdornment) && !isPasswordType;

    // Determine the actual input type
    const actualInputType = isPasswordType && showPassword ? "text" : type;

    return (
      <div className={cn("flex", inputVariants({ layout }), className)}>
        {label && (
          <label htmlFor={inputId} className="text-left text-muted-foreground text-sm min-w-[90px]">
            {label}
            {props.required && (
              <span aria-hidden className="ml-0.5">
                *
              </span>
            )}
          </label>
        )}
        <div className="w-full md:flex-1">
          <div className="relative">
            {isSearchType && (
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            )}
            <input
              id={inputId}
              ref={ref}
              type={actualInputType}
              aria-invalid={error ? "true" : undefined}
              className={cn(
                "w-full bg-muted text-foreground border border-input rounded-md px-3 py-2 focus:outline-none focus:border-primary",
                isSearchType && "pl-9",
                (isPasswordType || showEndAdornment) && "pr-10",
                error && "border-danger focus:border-danger",
                props.disabled && "opacity-50 cursor-not-allowed",
                "text-base md:text-sm",
                inputClassName
              )}
              {...(value !== undefined ? { value: value === null ? "" : value } : {})}
              {...props}
            />
            {isPasswordType && (
              <Button
                type="button"
                color="gray"
                size="icon"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-1/2 z-10 h-7 w-7 -translate-y-1/2 hover:scale-100"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            )}
            {showEndAdornment && (
              <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center">
                {endAdornment}
              </div>
            )}
          </div>
          {error && errorMessage && <div className="text-xs text-danger mt-1">{errorMessage}</div>}
        </div>
      </div>
    );
  }
);
InputV3.displayName = "InputV3";
